import {
  DatePipe,
  isPlatformBrowser,
} from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  inject,
  input,
  OnDestroy,
  PLATFORM_ID
} from '@angular/core';
import { NgxTolgeeModule } from '@tolgee/ngx';

import { RendezVous } from '../../../../core/model/rendez-vous.model';
import { emulateClick } from '../core/a11y';
import { DrawerService } from '../drawer/drawer.service';
import { NotificationService } from '../notification/notification.service';
import { HoverGradientDirective } from '../shared/directives/hover-gradient.directive';
import { ProgressClickDirective } from '../shared/directives/progress-click.directive';
import { AccountService } from '../shared/services/account.service';

@Component({
  selector: 'app-confirm-cancel-seance',
  imports: [
    HoverGradientDirective,
    NgxTolgeeModule,
    ProgressClickDirective,
    DatePipe
  ],
  templateUrl: './confirm-cancel-seance.component.html'
})
export class ConfirmCancelSeanceComponent implements OnDestroy {
  readonly emulateClick = emulateClick;
  accountService = inject(AccountService);
  drawerService = inject(DrawerService);
  notificationService = inject(NotificationService);
  rendezVous = input.required<RendezVous>()
  interval: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    if (isPlatformBrowser(inject(PLATFORM_ID))) {
      const cdRef = inject(ChangeDetectorRef);
      this.interval = setInterval(() => {
        cdRef.detectChanges();
      }, 1000)
    }
  }

  ngOnDestroy(): void {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  /**
   * Try to cancel a session
   */
  cancel = async (): Promise<void> => {
    try {
      await this.accountService.cancelSeance(this.rendezVous().erpId);
      this.drawerService.close();
      this.notificationService.open({
        type: 'success',
        title: '',
        message: 'cancelSuccess',
        close: 3000
      });
    } catch (err) {
      this.notificationService.open({
        type: 'error',
        title: 'cannotCancel',
        message: 'cannotCancelDescription'
      });
      return;
    }

    const now = new Date();
    const future = new Date()
    future.setFullYear(future.getFullYear() + 3);
    await Promise.all([
      this.accountService.rendezVous(
        now.toISOString().slice(0, 10),
        future.toISOString().slice(0, 10)
      ),
      this.refreshPlanning()
    ])
  }

  /**
   * Try to participate a session
   */
  cancelWait = async (): Promise<void> => {
    try {
      await this.accountService.cancelWaitSeance(this.rendezVous().erpId);
      this.drawerService.close();
      this.notificationService.open({
        type: 'success',
        title: '',
        message: 'cancelWaitSeance',
        close: 3000
      });
    } catch (err) {
      this.notificationService.open({
        type: 'error',
        title: 'cannotCancelWait',
        message: 'cannotCancelWaitDescription'
      });
      return;
    }
    const now = new Date();
    const future = new Date()
    future.setFullYear(future.getFullYear() + 3);
    await Promise.all([
      this.accountService.rendezVous(
        now.toISOString().slice(0, 10),
        future.toISOString().slice(0, 10)
      ),
      this.refreshPlanning()
    ])
  }

  lessThan24h(): boolean {
    const rdv = this.rendezVous();
    const [ rdvYear, rdvMonth, rdvDay ] = rdv.date.split('-').map(Number);
    const [ rdvHour, rdvMinute ] = rdv.start.split(':').map(Number);
    const now = Date.now();
    const rdvTime = Date.UTC(rdvYear, rdvMonth - 1, rdvDay, rdvHour, rdvMinute);
    return rdvTime - now < 24 * 60 * 60 * 1000;
  }

  private async refreshPlanning(): Promise<void> {
    this.accountService.dataChanged.set(new Date())
    await this.accountService.refreshCredits();
  }
}
